import React, { useState, useMemo } from 'react';
import { GameState, IncomingBid } from '../../domain/entities';
import { Modal } from '../shared/Modal';
import './PlayerDetailModals.css';

interface IncomingBidsModalProps {
    isOpen: boolean;
    onClose: () => void;
    gameState: GameState;
    onAcceptBid: (bidId: string) => void;
    onRejectBid: (bidId: string) => void;
    onNegotiateBid: (bidId: string, percentage: number) => void;
    onRespondToNegotiation: (bidId: string, accept: boolean) => void;
}

export const IncomingBidsModal: React.FC<IncomingBidsModalProps> = ({
    isOpen,
    onClose,
    gameState,
    onAcceptBid,
    onRejectBid,
    onNegotiateBid,
    onRespondToNegotiation
}) => {
    const [selectedBidId, setSelectedBidId] = useState<string | null>(null);
    const [negotiationPercentage, setNegotiationPercentage] = useState(0);
    const [activeTab, setActiveTab] = useState<'pending' | 'history'>('pending');

    // Get incomingBids safely with fallback to empty array
    const bids = gameState.incomingBids || [];

    // Filter bids by status
    const pendingBids = useMemo(() =>
        bids.filter(b => b.status === 'pending' || b.status === 'negotiating'),
        [bids]
    );

    const resolvedBids = useMemo(() =>
        bids.filter(b => b.status === 'accepted' || b.status === 'rejected'),
        [bids]
    );

    const selectedBid = bids.find(b => b.id === selectedBidId);

    const calculateNegotiatedAmount = (bid: IncomingBid, percentage: number): number => {
        return Math.round(bid.bidAmount * (1 + percentage / 100));
    };

    if (!isOpen || bids.length === 0) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <h2 style={{ marginBottom: '16px' }}>
                <i className="fas fa-gavel"></i> Transfer Bids
            </h2>

            {/* Tab Navigation */}
            <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '16px',
                borderBottom: '2px solid var(--accent-color-rgb)'
            }}>
                <button
                    onClick={() => { setActiveTab('pending'); setSelectedBidId(null); }}
                    style={{
                        padding: '8px 16px',
                        border: 'none',
                        background: activeTab === 'pending' ? 'var(--accent-color-rgb)' : 'transparent',
                        color: activeTab === 'pending' ? 'white' : 'var(--accent-color-rgb)',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '14px'
                    }}
                >
                    Pending ({pendingBids.length})
                </button>
                <button
                    onClick={() => { setActiveTab('history'); setSelectedBidId(null); }}
                    style={{
                        padding: '8px 16px',
                        border: 'none',
                        background: activeTab === 'history' ? 'var(--accent-color-rgb)' : 'transparent',
                        color: activeTab === 'history' ? 'white' : 'var(--accent-color-rgb)',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '14px'
                    }}
                >
                    History ({resolvedBids.length})
                </button>
            </div>

            <div style={{ display: 'flex', gap: '16px', minHeight: '300px' }}>
                {/* Bids List */}
                <div style={{ flex: '0 0 280px', maxHeight: '400px', overflowY: 'auto', borderRight: '1px solid var(--border-color)' }}>
                    {(activeTab === 'pending' ? pendingBids : resolvedBids).map(bid => (
                        <div
                            key={bid.id}
                            onClick={() => setSelectedBidId(bid.id)}
                            style={{
                                padding: '12px',
                                marginBottom: '8px',
                                background: selectedBidId === bid.id ? 'var(--accent-color-rgb)' : 'var(--panel-background)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <div style={{ fontWeight: 'bold', color: selectedBidId === bid.id ? 'white' : 'var(--text-color)' }}>
                                {bid.playerName}
                            </div>
                            <div style={{ fontSize: '12px', color: selectedBidId === bid.id ? 'rgba(255,255,255,0.8)' : 'var(--text-secondary)' }}>
                                {bid.biddingTeamName}
                            </div>
                            <div style={{ fontSize: '12px', marginTop: '4px', color: selectedBidId === bid.id ? 'rgba(255,255,255,0.8)' : 'var(--text-secondary)' }}>
                                £{bid.bidAmount.toLocaleString()}
                            </div>
                            {bid.status === 'negotiating' && (
                                <div style={{ fontSize: '11px', color: '#FFA500', marginTop: '4px' }}>
                                    ↔️ Negotiating
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Details Panel */}
                <div style={{ flex: 1 }}>
                    {selectedBid ? (
                        <div>
                            {/* Player Details */}
                            <div style={{ marginBottom: '16px' }}>
                                <h3 style={{ marginBottom: '8px' }}>{selectedBid.playerName}</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '14px' }}>
                                    <div>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Age</div>
                                        <div style={{ fontWeight: 'bold' }}>{selectedBid.playerAge}</div>
                                    </div>
                                    <div>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Position</div>
                                        <div style={{ fontWeight: 'bold' }}>{selectedBid.playerPosition}</div>
                                    </div>
                                    <div>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Skill</div>
                                        <div style={{ fontWeight: 'bold' }}>{selectedBid.playerSkill}</div>
                                    </div>
                                    <div>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Bidder</div>
                                        <div style={{ fontWeight: 'bold' }}>{selectedBid.biddingTeamName}</div>
                                    </div>
                                </div>
                            </div>

                            {/* Bid Amount */}
                            <div style={{ marginBottom: '16px', padding: '12px', background: 'var(--panel-background)', borderRadius: '4px' }}>
                                <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '4px' }}>Bid Amount</div>
                                <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--accent-color-rgb)' }}>
                                    £{selectedBid.bidAmount.toLocaleString()}
                                </div>
                            </div>

                            {/* Status-Specific Content */}
                            {selectedBid.status === 'pending' && (
                                <div>
                                    <div style={{ marginBottom: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                                        You can accept this offer, reject it, or negotiate for a higher price.
                                    </div>

                                    {/* Negotiation Options */}
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>Negotiate Amount</div>
                                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                            <button
                                                onClick={() => setNegotiationPercentage(10)}
                                                style={{
                                                    flex: 1,
                                                    padding: '8px',
                                                    background: negotiationPercentage === 10 ? 'var(--accent-color-rgb)' : 'var(--panel-background)',
                                                    color: negotiationPercentage === 10 ? 'white' : 'var(--text-color)',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                +10%
                                            </button>
                                            <button
                                                onClick={() => setNegotiationPercentage(20)}
                                                style={{
                                                    flex: 1,
                                                    padding: '8px',
                                                    background: negotiationPercentage === 20 ? 'var(--accent-color-rgb)' : 'var(--panel-background)',
                                                    color: negotiationPercentage === 20 ? 'white' : 'var(--text-color)',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                +20%
                                            </button>
                                            <button
                                                onClick={() => setNegotiationPercentage(0)}
                                                style={{
                                                    flex: 1,
                                                    padding: '8px',
                                                    background: negotiationPercentage === 0 ? 'var(--accent-color-rgb)' : 'var(--panel-background)',
                                                    color: negotiationPercentage === 0 ? 'white' : 'var(--text-color)',
                                                    border: '1px solid var(--border-color)',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                Reset
                                            </button>
                                        </div>
                                        {negotiationPercentage > 0 && (
                                            <div style={{ fontSize: '13px', padding: '8px', background: 'var(--panel-background)', borderRadius: '4px', marginBottom: '12px' }}>
                                                <div style={{ color: 'var(--text-secondary)', marginBottom: '4px' }}>Counter-offer:</div>
                                                <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--accent-color-rgb)' }}>
                                                    £{calculateNegotiatedAmount(selectedBid, negotiationPercentage).toLocaleString()}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {selectedBid.status === 'negotiating' && (
                                <div style={{ marginBottom: '16px' }}>
                                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                                        Counter-offer awaiting response...
                                    </div>
                                    <div style={{ padding: '12px', background: 'var(--panel-background)', borderRadius: '4px', marginBottom: '12px' }}>
                                        <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginBottom: '4px' }}>Your Request</div>
                                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--accent-color-rgb)' }}>
                                            £{selectedBid.lastNegotiationAmount?.toLocaleString()} (+{selectedBid.lastNegotiationPercentage}%)
                                        </div>
                                    </div>
                                </div>
                            )}

                            {selectedBid.status === 'accepted' && (
                                <div style={{ padding: '12px', background: 'rgba(0,128,0,0.1)', borderRadius: '4px', color: 'green', fontSize: '14px' }}>
                                    ✓ Transfer accepted and completed
                                </div>
                            )}

                            {selectedBid.status === 'rejected' && (
                                <div style={{ padding: '12px', background: 'rgba(255,0,0,0.1)', borderRadius: '4px', color: 'red', fontSize: '14px' }}>
                                    ✗ Bid rejected
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{ color: 'var(--text-secondary)', textAlign: 'center', paddingTop: '40px' }}>
                            Select a bid to view details
                        </div>
                    )}
                </div>
            </div>

            {/* Action Buttons */}
            <div style={{ marginTop: '16px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                {selectedBid && selectedBid.status === 'pending' && (
                    <>
                        <button
                            onClick={() => {
                                onRejectBid(selectedBid.id);
                                setSelectedBidId(null);
                            }}
                            style={{
                                padding: '8px 16px',
                                background: '#ff4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '13px'
                            }}
                        >
                            Reject
                        </button>
                        {negotiationPercentage > 0 && (
                            <button
                                onClick={() => {
                                    onNegotiateBid(selectedBid.id, negotiationPercentage);
                                    setNegotiationPercentage(0);
                                }}
                                style={{
                                    padding: '8px 16px',
                                    background: '#ff9900',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '13px'
                                }}
                            >
                                Counter-offer
                            </button>
                        )}
                        <button
                            onClick={() => {
                                onAcceptBid(selectedBid.id);
                                setSelectedBidId(null);
                            }}
                            style={{
                                padding: '8px 16px',
                                background: '#44aa44',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '13px'
                            }}
                        >
                            Accept
                        </button>
                    </>
                )}

                {selectedBid && selectedBid.status === 'negotiating' && (
                    <>
                        <button
                            onClick={() => {
                                onRespondToNegotiation(selectedBid.id, false);
                                setSelectedBidId(null);
                            }}
                            style={{
                                padding: '8px 16px',
                                background: '#ff4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '13px'
                            }}
                        >
                            Reject Counter
                        </button>
                        <button
                            onClick={() => {
                                onRespondToNegotiation(selectedBid.id, true);
                                setSelectedBidId(null);
                            }}
                            style={{
                                padding: '8px 16px',
                                background: '#44aa44',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '13px'
                            }}
                        >
                            Accept Counter
                        </button>
                    </>
                )}

                <button
                    onClick={onClose}
                    style={{
                        padding: '8px 16px',
                        background: 'var(--accent-color-rgb)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '13px'
                    }}
                >
                    Close
                </button>
            </div>
        </Modal>
    );
};
